import { mergeWith, get, isEmpty, find } from 'lodash';
import dayjs from 'dayjs';
import { getUserRoles, getCustomer, getUserType } from '../helpers/commonUtils.js';

window.previousState = window.previousState || {};
window.digitalData = {};
window.digitalData.allEventsStore = [];
window.digitalData.allPageLoadData = [];
window.digitalData.transactionProducts = [];
window.digitalData.transaction = [];
// Below code is push and persist the data onto allEventsStore until the session is cleared

['allEventsStore', 'allPageLoadData', 'transactionProducts', 'transaction'].forEach((item) => {
    if (!isEmpty(JSON.parse(sessionStorage.getItem(item)))) {
        const temp = JSON.parse(sessionStorage.getItem(item));
        window.digitalData[item] = [...temp];
    }
});

let isScriptLoaded = false;

/**
 * Add custom event to load the scripts related to Analytics
 * {trackingLibs} - which is a global variable which will contains the info of the required tracking libs
 */
if (typeof window !== 'undefined') {
    window.addEventListener('loadTrackingScript', () => {
        try {
            !isEmpty(window.trackingLibs) &&
                !isScriptLoaded &&
                Object.keys(trackingLibs).forEach((key) => {
                    const isScriptExist = document.getElementById(`tracking-${key}`);
                    if (!isScriptExist) {
                        loadScript(key);
                    }
                });
            isScriptLoaded = true;
        } catch (err) {
            console.error(err);
        }
    });

    document.body.addEventListener('click', () => {
        const { digitalData } = window;
        const eventInfo = get(digitalData, 'eventInfo', {});
        if (!isEmpty(eventInfo)) {
            digitalData.eventInfo = {};
        }
    });
}

/**
 * This function is takes the data to set in the dataLayer for GTM Tagging.
 * @param {Object} data - To set data in the dataLayer
 * @param {boolean} hasEvent - By default is false but when ever there is need to track an event It should be true to update the object.
 */
const track = (data, hasEvent = false) => {
    try {
        window.previousState = window.digitalData || {};
        const eventInfoData = data?.eventInfo || data?.pageLoadEventInfo || {};
        const eventInfoName = !!data?.eventInfo ? 'allEventsStore' : 'allPageLoadData';

        if (!isEmpty(eventInfoData)) {
            window.previousState[eventInfoName] = [
                ...window.previousState[eventInfoName],
                eventInfoData,
            ];
            if (!isEmpty(JSON.parse(sessionStorage.getItem(eventInfoName)))) {
                window.previousState[eventInfoName] = [
                    ...JSON.parse(sessionStorage.getItem(eventInfoName)),
                    eventInfoData,
                ];
                sessionStorage.setItem(
                    eventInfoName,
                    JSON.stringify(window.previousState[eventInfoName])
                );
            } else {
                sessionStorage.setItem(
                    eventInfoName,
                    JSON.stringify(window.previousState[eventInfoName])
                );
            }
        }
        if (!hasEvent && window.previousState && window.previousState.eventInfo) {
            window.previousState.eventInfo = {};
        }
        window.previousState.otherInfo = { eventDate: dayjs().format('YYYY-MM-DDTHH:mm:ssZ') };
        window.digitalData = mergeWith(window.previousState, data);
        customEventTrigger({
            event: 'trackData',
        });
    } catch (err) {
        console.log(err);
    }
};

const cacheUser = (deviceType) => {
    return (dispatch, getState) => {
        const state = getState();
        const user = get(state, 'AccountDetails.user', {});
        const useremail = get(user, 'email', null);
        const localStorageCustomer = (useremail && getCustomer(useremail)) || 'NA';
        const localStorageCustomerId = get(localStorageCustomer, 'customerid', 'NA');
        const customerId = get(state, 'meDetails.customer.id', localStorageCustomerId);
        user.language = get(state, 'meDetails.user.language', localStorageCustomerId);

        // Find user soldTo Country name based soldTo isoCode NAND: 06072023 for story S-106221.
        const soldToCountry = get(state, 'meDetails.customer.soldTo.address.country.isocode', '');
        const userSoldtoCountry = !!soldToCountry
            ? find(state?.commonData?.countryList || [], (country) => {
                  return country.isocode === soldToCountry;
              })?.name || ''
            : '';

        if (!isEmpty(user)) {
            const userRoles = get(state, 'AccountDetails.user.access', []);
            return setUserTrackInfo(user, userRoles, deviceType, customerId, userSoldtoCountry);
        }
        return setBeforeLogInInfo(deviceType);
    };
};

const cacheCustomTrackingInfo = () => {
    return (dispatch, getState) => {
        const state = getState();
        const defaultCustomer = get(state, 'meDetails.defaultCustomer', {});
        const customInfo = {
            soldToId: get(defaultCustomer, 'soldTo.id', 'NA'),
            payerId: get(defaultCustomer, 'supplierCustomerContracts[0][defaultPayer].id', 'NA'),
            shipToId: get(defaultCustomer, 'supplierCustomerContracts[0][defaultShipTo].id', 'NA'),
            billToId: get(defaultCustomer, 'supplierCustomerContracts[0][defaultBillTo].id', 'NA'),
        };
        return setCustomerTrackingInfo(customInfo);
    };
};

const setBeforeLogInInfo = (siteType, data = {}) => {
    try {
        if (window.cachedStateLessInfo || typeof window.digitalData === 'undefined') {
            return;
        }

        const loadTrackingScript = new CustomEvent('loadTrackingScript');
        track({
            siteInfo: {
                country: data.country || 'NA',
                siteLanguage: 'NA',
                siteType,
                soldtoCountry: 'NA',
            },
            userInfo: {
                userId: 'NA',
                userLanguage: 'NA',
                userRole: 'NA',
                customerId: 'NA',
                internalCustomer: 'NA',
            },
        });
        window.cachedStateLessInfo = !(Object.keys(data).length > 0);
        window.dispatchEvent(loadTrackingScript);
    } catch (error) {
        console.error(error);
    }
};

const setUserTrackInfo = (user, userRoles, siteType, customerId, userSoldtoCountry) => {
    try {
        const { language, pk } = user;
        const previousCustomer = get(window, 'digitalData.userInfo.customerId', 'NA');
        const previousLanguage = get(window, 'digitalData.userInfo.userLanguage', 'NA');
        if (
            (window.cachedUserInfo || typeof window.digitalData === 'undefined') &&
            previousCustomer === customerId &&
            previousLanguage === language
        ) {
            return;
        }
        const loadTrackingScript = new CustomEvent('loadTrackingScript');

        const useremail = get(user, 'email', null);
        const isInternal = useremail ? getUserType(useremail) : 'NA';

        track({
            siteInfo: {
                country: get(user, 'addresses[0].country.name', 'NA'),
                siteLanguage: (language && language.name) || (user && user.language) || 'NA',
                siteType,
                soldtoCountry: userSoldtoCountry || 'NA',
            },
            userInfo: {
                userId: pk || 'NA',
                userLanguage: get(language, 'name', 'NA'),
                userRole: userRoles && getUserRoles(userRoles),
                customerId,
                internalCustomer: isInternal,
            },
        });
        window.cachedUserInfo = true;
        if (!isScriptLoaded && !!loadTrackingScript) {
            window.dispatchEvent(loadTrackingScript);
        }
    } catch (error) {
        console.error(error);
    }
};

const setCustomerTrackingInfo = (customInfo) => {
    try {
        if (typeof window.digitalData === 'undefined') {
            return;
        }

        track({ customInfo });
        if (!isScriptLoaded) {
            window.dispatchEvent(loadTrackingScript);
        }
    } catch (error) {
        console.error(error);
    }
};

const loadScript = (key) => {
    try {
        const script = document.createElement('script');
        const head = document.getElementsByTagName('head')[0];
        const lib = !isEmpty(window.trackingLibs) && trackingLibs[key];
        const { dataLayerVariableName, dataLayerDefaultValue } = lib;

        script.setAttribute('id', `tracking-${key}`);
        if (lib.type === 'code') {
            script.innerText = lib.text;
        } else if (lib.type === 'script') {
            script.src = lib.text;
        }

        if (dataLayerVariableName) {
            window[dataLayerVariableName] =
                (dataLayerDefaultValue && JSON.parse(dataLayerDefaultValue)) || [];
        }
        head.appendChild(script);
    } catch (err) {
        console.log(err);
    }
};

/**
 * This function will be triggered after every API call success to read
 * the updated data from digitalData object.
 *
 * Insert custom trigger function here to get executed for different tracking
 * libraries.
 *
 * @param {dataObj} - Object - data which need to be pushed in the datalayer variable.
 *
 */
const customEventTrigger = (obj) => {
    // Below code will call the trigger function to track the data for each tracking lib
    try {
        !isEmpty(window.trackingLibs) &&
            Object.keys(window.trackingLibs).forEach((key) => {
                const lib = window.trackingLibs[key];
                const { triggerFunction } = lib;
                if (typeof triggerFunction === 'function') {
                    triggerFunction(obj);
                }
            });
    } catch (error) {
        console.log(error);
    }
};

export const MDTAnalytics = {
    cacheUser,
    track,
    cacheCustomTrackingInfo,
    setBeforeLogInInfo,
};
